"use client"

import { MagnifyingGlass, XMark } from "@medusajs/icons"
import { useRouter } from "next/navigation"
import { FormEvent, useEffect, useRef, useState } from "react"
import { searchProducts, type StorefrontSearchResult } from "@lib/data/search"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useGlobalAside } from "../../context/global-aside-context"
import Aside from "../aside"
import styles from "../aside/aside.module.css"

type SearchState = "idle" | "loading" | "ready" | "empty" | "error"

export default function SearchAside() {
  const { close, isOpen } = useGlobalAside()
  const router = useRouter()
  const [query, setQuery] = useState("")
  const [state, setState] = useState<SearchState>("idle")
  const [results, setResults] = useState<StorefrontSearchResult[]>([])
  const requestSequence = useRef(0)

  useEffect(() => {
    if (!isOpen("search")) return
    const trimmed = query.trim()
    const sequence = ++requestSequence.current

    if (trimmed.length < 2) {
      setResults([])
      setState("idle")
      return
    }

    const timer = window.setTimeout(async () => {
      setState("loading")
      try {
        const next = await searchProducts(trimmed)
        if (requestSequence.current !== sequence) return
        setResults(next)
        setState(next.length ? "ready" : "empty")
      } catch {
        if (requestSequence.current !== sequence) return
        setResults([])
        setState("error")
      }
    }, 250)

    return () => window.clearTimeout(timer)
  }, [isOpen, query])

  const closeSearch = () => close("search")
  const trimmed = query.trim()

  const clearSearch = () => {
    requestSequence.current += 1
    setQuery("")
    setResults([])
    setState("idle")
  }

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!trimmed) return
    router.push(`/search?q=${encodeURIComponent(trimmed)}`)
    closeSearch()
  }

  return (
    <Aside kind="search" label="Arama">
      <header className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>ARAMA</h2>
        <button type="button" className={styles.closeButton} onClick={closeSearch} aria-label="Aramayı kapat">
          <XMark aria-hidden="true" />
        </button>
      </header>
      <div className={`${styles.panelBody} ${styles.searchAside}`}>
        <form className={styles.searchForm} role="search" onSubmit={submitSearch}>
          <div className={styles.searchInputRow}>
            <span className={`${styles.searchInputLabel} ${trimmed ? styles.searchInputLabelHidden : ""}`} aria-hidden="true">
              <MagnifyingGlass />
            </span>
            <input
              className={styles.searchInput}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Arama"
              aria-label="Arama"
              autoComplete="off"
            />
            <span className={`${styles.searchResetSlot} ${trimmed ? styles.searchResetSlotVisible : ""}`}>
              <button
                type="button"
                className={styles.searchReset}
                onClick={clearSearch}
                aria-label="Aramayı temizle"
                disabled={!trimmed}
              >
                <XMark aria-hidden="true" />
              </button>
            </span>
            <button
              type="submit"
              className={`${styles.searchSubmit} ${trimmed ? styles.searchSubmitVisible : ""}`}
              aria-label="Ara"
            >
              <MagnifyingGlass aria-hidden="true" />
            </button>
          </div>
        </form>

        <div className={styles.searchResultsWrap}>
          {state === "loading" && <p role="status" className={styles.searchStatus}>Aranıyor…</p>}
          {state === "empty" && <p role="status" className={styles.searchStatus}>Sonuç bulunamadı.</p>}
          {state === "error" && <p role="status" className={styles.searchStatus}>Arama şu anda kullanılamıyor.</p>}

          {state === "ready" && (
            <ul className={styles.searchResults}>
              {results.map((result) => (
                <li key={result.id}>
                  <LocalizedClientLink
                    className={styles.searchResult}
                    href={`/products/${result.handle}`}
                    onClick={closeSearch}
                  >
                    {result.thumbnail ? (
                      <img className={styles.searchThumb} src={result.thumbnail} alt="" />
                    ) : (
                      <span className={styles.searchThumbPlaceholder} aria-hidden="true" />
                    )}
                    <span className={styles.searchResultTitle}>{result.title}</span>
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          )}

          {trimmed ? (
            <LocalizedClientLink
              className={styles.searchViewAll}
              href={`/search?q=${encodeURIComponent(trimmed)}`}
              onClick={closeSearch}
            >
              Tüm sonuçları gör <span className={styles.searchViewAllTerm}>“{trimmed}”</span>
            </LocalizedClientLink>
          ) : null}
        </div>
      </div>
    </Aside>
  )
}
